import React, { useCallback, useEffect } from 'react';

import { MEMBERSHIP } from '../../../../../constants/membership';
import text, { formatCurrency } from '../../../../../text';
import useUser from '../../../../hooks/useUser';
import { getUserPriceId } from '../../../../../utilities/user';
import {
  isMembershipPriceId,
  isYearlyMembershipPriceId,
} from '../../../../../utilities/membership';
import MembershipOption from './MembershipOption';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import MembershipCard from './MembershipCard';
import routes from '../../../../../routes/constants';

const memberships = {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  [MEMBERSHIP.MONTHLY.PRICE_ID!]: {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    priceId: MEMBERSHIP.MONTHLY.PRICE_ID!,
    info: [
      {
        firstHeader: text('essentials'),
        primaryText: text('monthlyMembershipRenews'),
      },
      {
        secondText: `/${text('month')}`,
        secondHeader: formatCurrency(90, 'aud', 1, {}, true),
        supplementalText: `*${text('minPriceAmount', {
          amount: formatCurrency(
            1080,
            'aud',
            1,
            { maximumFractionDigits: 0 },
            true
          ),
        })}`,
      },
    ],
  },

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  [MEMBERSHIP.YEARLY.PRICE_ID!]: {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    priceId: MEMBERSHIP.YEARLY.PRICE_ID!,
    info: [
      {
        firstHeader: text('essentials'),
        primaryText: text('annualMembershipRenews'),
      },
      {
        secondHeader: formatCurrency(75, 'aud', 1, {}, true),
        secondText: `/${text('monthEquivalent')}*`,
        supplementalText: `*${text('paidUpfrontAmount', {
          months: 12,
          amount: formatCurrency(
            900,
            'aud',
            1,
            { maximumFractionDigits: 0 },
            true
          ),
        })}`,
      },
    ],
  },
  [MEMBERSHIP.NO_MEMBERSHIP]: {
    priceId: MEMBERSHIP.NO_MEMBERSHIP,
    info: [
      {
        firstHeader: text('noMembership'),
        primaryText: text('noMembershipFeatures'),
      },
    ],
  },
};

const membershipOptions = {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  [MEMBERSHIP.YEARLY.PRICE_ID!]: {
    label: text('yearlySaveDiscount', { discount: 17 }),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    priceId: MEMBERSHIP.YEARLY.PRICE_ID!,
  },
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  [MEMBERSHIP.MONTHLY.PRICE_ID!]: {
    label: text('monthly'),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    priceId: MEMBERSHIP.MONTHLY.PRICE_ID!,
  },
  [MEMBERSHIP.NO_MEMBERSHIP]: {
    label: text('noThanks'),
    priceId: MEMBERSHIP.NO_MEMBERSHIP,
  },
};

const Membership = () => {
  const membershipPriceId = useAppSelector(
    (state) => state.order.membershipPriceId
  );
  return (
    <div className='MembershipForm-card'>
      <MembershipCard {...memberships[membershipPriceId]} />
      {isMembershipPriceId(membershipPriceId) && (
        <div className='termsAndCond'>
          <div>{`*${text('pricingTax')}`}</div>
          <a
            href={routes.larkiWebsiteUrl.custTermsCond}
            target='_blank'
            rel='noreferrer'
          >
            {text('larkiTermsAndCond')}
          </a>
        </div>
      )}
    </div>
  );
};

const MembershipForm = ({
  membershipPriceId,
  onMembershipChange,
  includeNoMembership = true,
}: {
  membershipPriceId: string;
  onMembershipChange: (membershipPriceId: string) => void;
  includeNoMembership?: boolean;
}) => {
  const currentUser = useUser();
  const noMembershipOption = membershipOptions[MEMBERSHIP.NO_MEMBERSHIP];

  useEffect(() => {
    // Skip if no active memberships
    if (currentUser.activeMemberships?.length === 0) return;

    // Skip if user has explicitly selected "No thanks"
    if (membershipPriceId === MEMBERSHIP.NO_MEMBERSHIP) return;

    const userPriceId = getUserPriceId(currentUser.profile);
    if (!userPriceId) return;

    const isCurrentYearly = isYearlyMembershipPriceId(userPriceId);
    const targetPriceId = isCurrentYearly
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        MEMBERSHIP.MONTHLY.PRICE_ID!
      : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        MEMBERSHIP.YEARLY.PRICE_ID!;

    // Only change if we're not already on the target membership type
    if (membershipPriceId !== targetPriceId) {
      onMembershipChange(targetPriceId);
    }
  }, [
    currentUser.activeMemberships,
    currentUser.profile,
    membershipPriceId,
    onMembershipChange,
  ]);

  return (
    <div className='MembershipForm'>
      <div className='MembershipForm-options'>
        <div>
          <div className='options-label'>{`${text('billed')}:`}</div>
          {[
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            membershipOptions[MEMBERSHIP.YEARLY.PRICE_ID!],
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            membershipOptions[MEMBERSHIP.MONTHLY.PRICE_ID!],
          ]
            .filter(
              ({ priceId }) =>
                !currentUser.activeMemberships?.some(
                  (m) => m.price_id === priceId
                )
            )
            .map(({ label, priceId }, index) => (
              <MembershipOption
                onChange={onMembershipChange}
                key={index}
                label={label}
                priceId={priceId}
                isSelected={membershipPriceId === priceId}
                noMembership={!isMembershipPriceId(priceId)}
              />
            ))}
          {includeNoMembership ? (
            <MembershipOption
              onChange={onMembershipChange}
              label={noMembershipOption.label}
              priceId={noMembershipOption.priceId}
              isSelected={membershipPriceId === noMembershipOption.priceId}
              noMembership={!isMembershipPriceId(noMembershipOption.priceId)}
            />
          ) : null}
        </div>
      </div>
      <Membership />
    </div>
  );
};

export default MembershipForm;
